import type {
  ConfiguracaoDoAdmin,
  ContextoDoAdmin,
  DadosDeInicioDoAdmin,
  EstadoSimulacao,
  EstadoSimulacaoAdmin,
  ModoDaSimulacao,
  ModoDisponivel,
  MotivoDeInicio,
  OpcaoDeModo,
  ParametrosDaSimulacao,
} from '../types';
import {
  dataHoraDoEvento,
  ROTULO_DO_MODO,
  textoDaIndisponibilidadeDoModo,
} from './rotulosDaSimulacao';

/**
 * O estado da superfície ADMIN traz o contexto da execução; o do cliente não tem esses campos. O
 * fluxo inicial nulo (fluxo apagado depois do início) continua sendo do admin: a faixa e a
 * exportação não podem sumir por isso.
 */
export const ehEstadoDoAdmin = (estado: EstadoSimulacao | null): estado is EstadoSimulacaoAdmin => {
  if (!estado) return false;
  const campos: Record<string, unknown> = { ...estado };
  return (
    (campos.modo === 'RASCUNHO' || campos.modo === 'PUBLICADA') &&
    (typeof campos.fluxoInicialId === 'string' || campos.fluxoInicialId === null) &&
    typeof campos.escritorioId === 'string'
  );
};

export const modoDaConfiguracao = (
  configuracao: ConfiguracaoDoAdmin,
  modo: ModoDaSimulacao,
): ModoDisponivel => (modo === 'RASCUNHO' ? configuracao.rascunho : configuracao.publicada);

const MODOS: ModoDaSimulacao[] = ['RASCUNHO', 'PUBLICADA'];

/** Só a opção indisponível sai desabilitada, com o motivo junto (CA-15). */
export const opcoesDeModo = (configuracao: ConfiguracaoDoAdmin | null): OpcaoDeModo[] =>
  MODOS.map((modo) => {
    const disponibilidade = configuracao ? modoDaConfiguracao(configuracao, modo) : null;
    return {
      modo,
      rotulo: ROTULO_DO_MODO[modo],
      disponivel: disponibilidade?.disponivel === true,
      motivo:
        disponibilidade && !disponibilidade.disponivel
          ? textoDaIndisponibilidadeDoModo(disponibilidade.motivo)
          : null,
    };
  });

/**
 * Corpo do POST / do admin. A revisão e a versão esperadas são as da configuração lida agora: se o
 * core tiver outra, ele recusa com 409, e nada roda com uma configuração que a tela não mostrou
 * (CA-14). Fluxo sem agente não tem versão publicada para conferir.
 */
export const corpoDoInicioDoAdmin = (
  contexto: ContextoDoAdmin | null,
  configuracao: ConfiguracaoDoAdmin | null,
  parametros: ParametrosDaSimulacao,
  motivo: MotivoDeInicio,
): DadosDeInicioDoAdmin | null => {
  if (!contexto?.escritorioId || !contexto.modo || !configuracao) return null;
  const { revisaoRascunho, versaoId } = modoDaConfiguracao(configuracao, contexto.modo);
  return {
    flowId: contexto.flowId,
    escritorioId: contexto.escritorioId,
    modo: contexto.modo,
    ...(contexto.modo === 'RASCUNHO' && revisaoRascunho !== null
      ? { revisaoEsperada: revisaoRascunho }
      : {}),
    ...(contexto.modo === 'PUBLICADA' && configuracao.agenteId !== null && versaoId !== null
      ? { versaoEsperadaId: versaoId }
      : {}),
    parametros,
    motivo,
  };
};

/** A data é a da publicação que a execução usou: a de outra versão não vale para ela. */
const publicacaoDaExecucao = (
  estado: EstadoSimulacaoAdmin,
  configuracao: ConfiguracaoDoAdmin | null,
): string | null =>
  configuracao?.publicada.versaoId === estado.agenteVersaoId && configuracao.publicada.publicadaEm
    ? configuracao.publicada.publicadaEm
    : null;

/** Modo e revisão ou versão lidos da execução, nunca da configuração de agora (CA-12). */
export const identificacaoDaExecucao = (
  estado: EstadoSimulacaoAdmin,
  configuracao: ConfiguracaoDoAdmin | null,
): string => {
  const modo = ROTULO_DO_MODO[estado.modo];
  if (estado.modo === 'RASCUNHO') {
    return estado.revisaoRascunho === null ? modo : `${modo} · revisão r${estado.revisaoRascunho}`;
  }
  if (estado.agenteId === null || estado.agenteVersao === null) {
    return `${modo} · definição salva do fluxo`;
  }
  const publicadaEm = publicacaoDaExecucao(estado, configuracao);
  const data = publicadaEm ? ` · publicada em ${dataHoraDoEvento(publicadaEm)}` : '';
  return `${modo} · v${estado.agenteVersao}${data}`;
};

/** Cabeçalho do MD e do PDF do admin: de onde veio a conversa exportada (CA-6). */
export const linhasDoAdminParaExportacao = (
  estado: EstadoSimulacaoAdmin,
  configuracao: ConfiguracaoDoAdmin | null,
): string[] => [
  `Escritório: ${estado.escritorioId}`,
  `Fluxo inicial: ${estado.fluxoInicialId ?? 'excluído'}`,
  `Configuração: ${identificacaoDaExecucao(estado, configuracao)}`,
  ...(estado.configuracaoAlterada
    ? ['Aviso: a configuração foi salva depois do início do teste.']
    : []),
];
